/**
 * Server state and URL state for the QA home (docs/ux/qa.md): one query for
 * the four queues, one owner of `?queue`, `?q`, `?doctor`, `?risk`, `?sort`
 * and `?dir` so a pinned tab reopens on the same queue and filters.
 */

import { defaultOrder } from '../pipeline/views';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { api } from '@/app/api';
import type { QaQueues, QueueRow } from '@/app/types';
import { matchesQuery } from '@/features/pipeline/views';

export const qaQueuesKey = ['qa', 'queues'] as const;

export function useQaQueues() {
  return useQuery({ queryKey: qaQueuesKey, queryFn: () => api<QaQueues>('/api/qa/queues'), staleTime: 10_000, retry: (count) => count < 1 });
}

export type QueueId = 'report' | 'sort' | 'loi' | 'clarifications' | 'bridge';
/** The tabs: All first, then the queues in the order the work reaches QA in the status flow (D-0066). */
export type QueueTab = 'all' | QueueId;
export const QUEUE_IDS: readonly QueueId[] = ['sort', 'loi', 'report', 'clarifications', 'bridge'];
export const TAB_IDS: readonly QueueTab[] = ['all', ...QUEUE_IDS];
export const QUEUE_LABEL: Record<QueueTab, string> = { all: 'All', report: 'Report QA', sort: 'Sort review', loi: 'LOI review', clarifications: 'Clarifications', bridge: 'Bridge' };

/** A row on the All tab remembers the queue it came from, so its target, words and card are that queue's. */
export type TabRow = QueueRow & { queue?: QueueId };

export function allRows(data: QaQueues | undefined): TabRow[] {
  if (!data) return [];
  const rows: TabRow[] = [];
  for (const id of QUEUE_IDS) for (const r of data[id]) rows.push({ ...r, queue: id });
  // The board's order, so a queue and the board agree on what is at the top (docs/ux/pipeline.md).
  return rows.toSorted((a, b) => defaultOrder(a, b) || a.ref.localeCompare(b.ref));
}

export const rowQueue = (tab: QueueTab, row: TabRow): QueueId => (tab === 'all' ? row.queue ?? 'report' : tab);
export const rowId = (row: TabRow): string => (row.queue ? `${row.queue}:${row.ref}` : row.ref);

/** The four tiles are all filters on the current queue; pressing the pressed one clears it. */
export type Risk = 'overdue' | 'due48' | 'awaiting_qa' | 'with_doctor' | 'past_mark';
export const RISKS: readonly Risk[] = ['awaiting_qa', 'with_doctor', 'overdue', 'due48', 'past_mark'];

export type QaFilters = { queue: QueueTab; q: string; doctor: string | null; risk: Risk | null; sort: string | null; dir: 'asc' | 'desc' };
export const EMPTY_QA_FILTERS: QaFilters = { queue: 'all', q: '', doctor: null, risk: null, sort: null, dir: 'asc' };

export function parseQaFilters(params: URLSearchParams): QaFilters {
  const queue = params.get('queue');
  const risk = params.get('risk');
  return {
    queue: queue && (TAB_IDS as readonly string[]).includes(queue) ? (queue as QueueTab) : 'all',
    q: (params.get('q') ?? '').slice(0, 64),
    doctor: params.get('doctor') || null,
    risk: risk && (RISKS as readonly string[]).includes(risk) ? (risk as Risk) : null,
    sort: params.get('sort') || null,
    dir: params.get('dir') === 'desc' ? 'desc' : 'asc',
  };
}

export function serialiseQaFilters(f: QaFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.queue !== 'all') p.set('queue', f.queue);
  if (f.q.trim()) p.set('q', f.q.trim());
  if (f.doctor) p.set('doctor', f.doctor);
  if (f.risk) p.set('risk', f.risk);
  if (f.sort) { p.set('sort', f.sort); if (f.dir === 'desc') p.set('dir', 'desc'); }
  return p;
}

export function activeQaFilters(f: QaFilters): number {
  return [f.q.trim(), f.doctor, f.risk].filter(Boolean).length;
}

export function useQaFilters() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => parseQaFilters(params), [params]);
  const update = useCallback((patch: Partial<QaFilters>) => {
    setParams((prev) => serialiseQaFilters({ ...parseQaFilters(prev), ...patch }), { replace: true });
  }, [setParams]);
  const clear = useCallback(() => {
    setParams((prev) => serialiseQaFilters({ ...EMPTY_QA_FILTERS, queue: parseQaFilters(prev).queue }), { replace: true });
  }, [setParams]);
  return { filters, update, clear };
}

/** The 48 hour window, read to the end of the Perth working day as the server does. */
export function dueWithin48h(dueDate: string, now: Date): boolean {
  const gap = new Date(`${dueDate}T17:00:00.000+08:00`).getTime() - now.getTime();
  return gap >= 0 && gap <= 48 * 3_600_000;
}

export function riskMatches(row: QueueRow, risk: Risk | null, now: Date): boolean {
  if (!risk) return true;
  if (risk === 'overdue') return row.slaState === 'overdue';
  if (risk === 'due48') return dueWithin48h(row.dueDate, now);
  if (risk === 'past_mark') return row.pastReportMark;
  if (risk === 'awaiting_qa') return row.status === 7;
  return row.status === 8;
}

/** The server's order is due date then Urgent; the column sort is the table's own. The search is the pipeline's (a bare number matches the reference's end). */
export function applyQueueFilters(rows: QueueRow[], f: QaFilters, now: Date = new Date()): QueueRow[] {
  return rows.filter((r) => matchesQuery(r, f.q) && (!f.doctor || r.doctor.slug === f.doctor) && riskMatches(r, f.risk, now));
}

/** Where a row opens: the work, not the matter (docs/ux/qa.md section 1). */
export function queueTarget(queue: QueueId, row: QueueRow): string {
  const ref = encodeURIComponent(row.ref);
  if (queue === 'sort') return `/matters/${ref}?tab=casefile`;
  if (queue === 'loi') return `/matters/${ref}?tab=loi`;
  if (queue === 'bridge') return `/matters/${ref}?tab=bridge`;
  return `/matters/${ref}/workspace`;
}
